<template>
  <page-status class="proposal-sign">
    <div class="header">
      <span>请投保人</span>
      <span class="active">{{ userName }}</span>
      <span>在输入框中签字</span>
    </div>

    <div class="notice">请用正楷签名，每次只需要签一个字</div>

    <div class="sign-container">
      <canvas
        style="background: #ffffff"
        ref="canvas"
        width="200"
        height="200"
      ></canvas>
      <div class="sign-content">
        <canvas
          ref="canvasBackground"
          style="width: 90vw; height: 90vw"
        ></canvas>
        <vue-signature-pad
          class="recorder-signature-pad"
          style="position: relative; top: -90vw"
          width="100%"
          height="100%"
          ref="signaturePad"
          :options="{ onEnd, minWidth: 1, maxWidth: 5 }"
        />
      </div>
    </div>

    <div class="sign-result">
      <div class="result-title">已输入文字</div>
      <div class="images">
        <img v-for="(item, index) in images" :key="index" :src="item" alt="" />
      </div>
    </div>

    <div class="sign-actions">
      <van-button class="button" type="default" @click="fetchData()">
        重签
      </van-button>
      <van-button
        :disabled="this.userName.length !== this.images.length"
        class="button"
        type="info"
        @click="onConfirmSignature()"
      >
        提交
      </van-button>
    </div>
  </page-status>
</template>

<script>
import { Toast, Dialog } from 'vant';
import * as VueSignaturePadModule from 'vue-signature-pad';
import PageStatus from '@/components/PageStatus';
import * as services from '@/services';
import { bindRecord, emitRecorderCustomEvent } from '@/core/recorder';

const SIGNATURE_CANVAS_KEY = 'signature-pad';
const SIGNATURE_PAYLOAD_VERSION = 1;
// 兼容 package-lock 中 2.0.0 的默认导出和 pnpm-lock 中 2.0.5 的具名导出。
const VueSignaturePad =
  VueSignaturePadModule.VueSignaturePad ||
  VueSignaturePadModule.default ||
  VueSignaturePadModule;

export default {
  name: 'Signature',
  components: {
    VueSignaturePad,
    PageStatus,
  },

  data() {
    return {
      images: [],
      userName: '',
    };
  },

  computed: {
    unreadItem() {
      return this.detail.clauseVos.find(item => !item.clicked);
    },
  },

  watch: {
    checked(newValue) {
      if (this.unreadItem && newValue) {
        this.showDialog = true;
        this.checked = false;
      }
    },
  },

  created() {
    const token =
      this.$route.query.authToken || window.localStorage.getItem('authToken');
    window.localStorage.setItem('authToken', token);

    const { businessNo, mobile } = this.$route.query;
    if (businessNo && mobile) {
      bindRecord({ userId: businessNo + '_' + mobile });
    }

    this.fetchData();
  },

  mounted() {
    // vue-signature-pad 在自身 mounted 中创建内部 <canvas>，nextTick 后即可访问。
    this.$nextTick(this.attachSignatureRecorder);
  },

  beforeDestroy() {
    this.detachSignatureRecorder();
  },

  methods: {
    // ---- 签名笔画录制（自定义事件） -----------------------------------------
    // 仅通过 Pointer Events 在真实的内部 <canvas> 上逐条采集笔画，
    // 每个浏览器事件都作为独立自定义事件发送（不做 RAF 合批）。
    // 不调用 resizeSignaturePad，也不修改签名区的尺寸。
    attachSignatureRecorder() {
      const pad = this.$refs.signaturePad;
      const canvas = pad && pad.$refs && pad.$refs.signaturePadCanvas;
      if (!canvas || this._sigCanvas === canvas) return;

      this._sigCanvas = canvas;
      // 直接挂在实例上，避开 Vue 响应式，避免高频 pointer 事件的额外开销。
      this._sigState = {
        canvasKey: SIGNATURE_CANVAS_KEY,
        strokeId: 0,
        activePointerId: null,
        hasPoints: false, // 自上次 clear 起是否已记录过笔画点
      };

      // bind 后保存引用，便于精确 removeEventListener。
      this._sigOnPointerDown = e => this.handleSignaturePointerDown(e);
      this._sigOnPointerMove = e => this.handleSignaturePointerMove(e);
      this._sigOnPointerUp = e => this.handleSignaturePointerUp(e);
      this._sigOnPointerCancel = e => this.handleSignaturePointerCancel(e);

      canvas.addEventListener('pointerdown', this._sigOnPointerDown);
      canvas.addEventListener('pointermove', this._sigOnPointerMove);
      canvas.addEventListener('pointerup', this._sigOnPointerUp);
      canvas.addEventListener('pointercancel', this._sigOnPointerCancel);
    },

    detachSignatureRecorder() {
      const canvas = this._sigCanvas;
      if (!canvas) return;
      if (this._sigOnPointerDown) {
        canvas.removeEventListener('pointerdown', this._sigOnPointerDown);
      }
      if (this._sigOnPointerMove) {
        canvas.removeEventListener('pointermove', this._sigOnPointerMove);
      }
      if (this._sigOnPointerUp) {
        canvas.removeEventListener('pointerup', this._sigOnPointerUp);
      }
      if (this._sigOnPointerCancel) {
        canvas.removeEventListener('pointercancel', this._sigOnPointerCancel);
      }
      this._sigCanvas = null;
      this._sigState = null;
    },

    // 计算归一化坐标（0~1，含夹紧），返回 { x, y, width, height }。
    signatureNormalizedPoint(e) {
      const canvas = this._sigCanvas;
      const rect = canvas
        ? canvas.getBoundingClientRect()
        : { left: 0, top: 0, width: 0, height: 0 };
      const width = rect.width || (canvas ? canvas.offsetWidth : 0) || 0;
      const height = rect.height || (canvas ? canvas.offsetHeight : 0) || 0;
      let x = width > 0 ? (e.clientX - rect.left) / width : 0;
      let y = height > 0 ? (e.clientY - rect.top) / height : 0;
      if (x < 0) x = 0;
      else if (x > 1) x = 1;
      if (y < 0) y = 0;
      else if (y > 1) y = 1;
      return { x, y, width, height };
    },

    emitSignaturePoint(phase, e) {
      const st = this._sigState;
      if (!st) return;
      const { x, y, width, height } = this.signatureNormalizedPoint(e);
      emitRecorderCustomEvent('signature-point', {
        v: SIGNATURE_PAYLOAD_VERSION,
        canvasKey: st.canvasKey,
        strokeId: st.strokeId,
        phase,
        x,
        y,
        width,
        height,
        t: typeof e.timeStamp === 'number' ? e.timeStamp : 0,
      });
      st.hasPoints = true;
    },

    handleSignaturePointerDown(e) {
      const st = this._sigState;
      if (!st) return;
      // 鼠标右键/中键忽略；签名只支持单笔。
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (st.activePointerId != null) return;
      st.activePointerId = e.pointerId;
      st.strokeId += 1;
      this.emitSignaturePoint('start', e);
    },

    handleSignaturePointerMove(e) {
      const st = this._sigState;
      if (!st || st.activePointerId !== e.pointerId) return;
      // 一次浏览器 pointermove 对应一条 rrweb 自定义事件。不要在这里展开
      // coalesced events：SDK 会在同一调用栈内为它们写入相同时间戳，回放时
      // 会再次成批闪现，失去逐笔的时间节奏。
      this.emitSignaturePoint('move', e);
    },

    handleSignaturePointerUp(e) {
      const st = this._sigState;
      if (!st || st.activePointerId !== e.pointerId) return;
      this.emitSignaturePoint('end', e); // 包含收笔的最终点
      st.activePointerId = null;
    },

    handleSignaturePointerCancel(e) {
      const st = this._sigState;
      if (!st || st.activePointerId !== e.pointerId) return;
      this.emitSignaturePoint('end', e); // 取消时同样以 end 收尾
      st.activePointerId = null;
    },

    // 实际签名画布被清空时调用：仅当本轮已记录过笔画时发送 clear，
    // 避免在尚未书写前产生误导性的初始 clear。
    emitSignatureClearIfDirty() {
      const st = this._sigState;
      if (!st || !st.hasPoints) return;
      emitRecorderCustomEvent('signature-point', {
        v: SIGNATURE_PAYLOAD_VERSION,
        canvasKey: st.canvasKey,
        strokeId: st.strokeId,
        phase: 'clear',
      });
      st.hasPoints = false;
      st.activePointerId = null;
    },

    onEnd() {
      this.t && clearTimeout(this.t);

      const signaturePad = this.$refs.signaturePad;
      const { isEmpty, data } = signaturePad.saveSignature();
      if (isEmpty) return;

      this.t = setTimeout(() => {
        this.setImgBg(data).then(dataUrl => {
          this.discernSigned(dataUrl);
        });
      }, 2000);
    },

    setImgBg(data) {
      return new Promise(resolve => {
        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 200, 200);
        const img = new Image();
        img.src = data;
        img.onload = () => {
          ctx.drawImage(img, 0, 0, 200, 200);
          const dataUrl = canvas.toDataURL('image/jpeg');
          resolve(dataUrl);
        };
      });
    },

    discernSigned(data) {
      Toast.loading({
        message: '正在验证...',
        forbidClick: true,
        duration: 0,
      });

      const file = this.dataURLtoFile(data);
      const fileIndex = this.images.length;
      const { businessNo, mobile } = this.$route.query;

      services.discernSigned(businessNo, mobile, file, fileIndex).then(res => {
        if (res.data) {
          this.images.push(data);
          this.onClearSignature();
          this.draw(fileIndex + 1);
        } else {
          Dialog.alert({
            message: '签名内容非投保人姓名，请使用正楷字体重新签署。',
          }).then(() => {
            this.onClearSignature();
          });
        }
        Toast.clear();
      });
    },

    onClearSignature() {
      // 实际签名画布即将被清空，让回放状态跟随真实屏幕。
      this.emitSignatureClearIfDirty();
      this.$refs.signaturePad.clearSignature();
    },

    fetchData() {
      const { businessNo } = this.$route.query;

      Toast.loading({
        message: '加载中...',
        forbidClick: true,
        duration: 0,
      });

      services.getSignedInit(businessNo).then(res => {
        this.userName = res.data;
        // 重签场景：实际签名画布被清空。
        this.emitSignatureClearIfDirty();
        this.$refs.signaturePad && this.$refs.signaturePad.clearSignature();
        this.images = [];
        Toast.clear();
      });
      setTimeout(() => this.draw(0), 1000);
    },
    draw(fileIndex) {
      const canvas = this.$refs.canvasBackground;
      const ctx = canvas.getContext('2d');
      const text = this.userName[fileIndex];
      ctx.fillStyle = '#ffffff';
      const width = canvas.width;
      const height = canvas.height;
      ctx.fillRect(0, 0, width, height);
      const img = new Image();
      img.src =
        'https://i3.cathay-ins.com.cn/prod/posts/e73e20dd1ecd6cd05f0bdab1fd75e78e.png';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        ctx.font = '150px Georgia,serif';
        ctx.fontWeight = 'bold';
        ctx.fillStyle = '#ddd';
        if (text) {
          ctx.fillText(text, 80, 130);
        }
      };
    },

    dataURLtoFile(dataURL) {
      const parts = dataURL.split(';base64,');
      const contentType = parts[0].split(':')[1];
      const raw = window.atob(parts[1]);
      const rawLength = raw.length;
      const uInt8Array = new Uint8Array(rawLength);

      for (let i = 0; i < rawLength; i++) {
        uInt8Array[i] = raw.charCodeAt(i);
      }

      const filename = `${Date.now()}.png`;
      const blob = new Blob([uInt8Array], { type: contentType });
      return new File([blob], filename, { type: contentType });
    },

    onConfirmSignature() {
      const {
        businessNo,
        mobile,
        realAuthSmsFlag,
        comCode,
        authToken,
      } = this.$route.query;
      const confirmParams = {
        title: '',
        allowHtml: true,
        message: `<div style="text-align: left; white-space: normal;">
            <div>根据行业监管需求，《免责事项说明书》中一下黑色字体内容需进行抄写，点击确认系统将自动抄写，表明您已了解投保内容并自愿投保。\n</div>
            <div style="font-weight: bold; margin-top: 8px;">保险人已明确说明免除保险人责任条款的内容及法律后果</div>
          </div>`,
      };

      Dialog.confirm(confirmParams)
        .then(() =>
          Toast.loading({
            message: '正在提交...',
            forbidClick: true,
            duration: 0,
          }),
        )
        .then(() => services.uploadSigned(businessNo))
        .then(res => {
          Toast.clear();
          const checkMessageFlag = res.data.checkMessageFlag ? 1 : 0;
          if (res.data && res.data.payType === 'Bill99Pay') {
            this.getMotorPayUrl(businessNo);
          } else if (res.data && res.data.payType === 'ANT') {
            this.getMotorPayUrl(businessNo);
          } else if (res.data && comCode === '909') {
            this.$router.replace({
              path: '/new-order-pay',
              query: {
                checkMessageFlag,
                bizNo: businessNo,
                bizType: 'T',
                mobile,
                realAuthSmsFlag,
                authToken,
              },
            });
          } else {
            this.$router.replace({
              path: '/signature-result',
              query: {
                checkMessageFlag,
                businessNo,
                mobile,
                realAuthSmsFlag,
              },
            });
          }
        })
        .catch(() => {});
    },

    getMotorPayUrl(businessNo) {
      services.getMotorPayUrl(businessNo).then(res => {
        const url = res.data;
        if (url) {
          window.location.href = url;
        }
        Toast.clear();
      });
    },
  },
};
</script>

<style lang="less">
.proposal-sign {
  width: 100%;
  box-sizing: border-box;
  padding-bottom: 94px;
  -moz-user-select: none;
  -o-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  background-color: #fff;

  .header {
    height: 48px;
    line-height: 48px;
    background-color: #ffffff;
    box-shadow: 0px 2px 4px 0px rgba(48, 49, 51, 0.1);
    text-align: center;
    color: #434b65;
    font-size: 16px;
    .active {
      color: #ad3b1b;
      margin: 0 4px;
    }
  }

  .notice {
    height: 38px;
    line-height: 38px;
    background-color: #fff9ed;
    text-align: center;
    font-size: 14px;
    color: #ff6010;
    border-top: solid 1px #eeeeee;
  }

  .sign-container {
    width: 92vw;
    height: 92vw;
    margin: 4vw;
    position: relative;

    canvas {
      width: 92vw;
      height: 92vw;
    }
  }
  .sign-background {
    position: absolute;
    top: 0;
  }

  .sign-content {
    width: 92vw;
    height: 92vw;
    /**background-image: url('https://i3.cathay-ins.com.cn/prod/posts/e73e20dd1ecd6cd05f0bdab1fd75e78e.png');**/
    background-size: contain;
    background-repeat: no-repeat;
    border: 1px solid #dcdcdc;
    border-radius: 4px;
    margin: 0 auto;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
  }

  .sign-actions {
    padding: 16px 16px 30px;
    display: flex;
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    box-sizing: border-box;
    background-color: #fff;
    border-top: solid 1px #eeeeee;

    .button {
      flex: 1;
    }

    .button + .button {
      margin-left: 12px;
    }
  }

  .sign-result {
    display: flex;
    flex-direction: column;
    text-align: center;
    .result-title {
      font-size: 14px;
      color: #808897;
      margin-bottom: 10px;
    }
    .images {
      img {
        width: 40px;
        height: 40px;
      }
    }
  }
}
</style>
